import { Button } from "@progress/kendo-react-buttons";
import { TextBox } from "@progress/kendo-react-inputs";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { GridCustomFilterCellProps } from "@progress/kendo-react-grid";
import { RootState } from "../../app/store";
import { setCurrentPage } from "../../features/pagination/paginationSlice";
import _debounce from "lodash/debounce";

interface DynamicFilterCellProps extends GridCustomFilterCellProps {
  filterSelector?: (state: RootState) => any;
  setFilterAction?: (payload: any) => any;
  clearFilterAction?: () => any;
  fetchDataAction?: (payload: any) => any;
  pagination?: {
    case: "lowercase" | "capitalize" | "uppercase";
  };
  numberfiels?: string[];
}
const CommonFilterCell = (props: DynamicFilterCellProps) => {
  const dispatch = useAppDispatch();
  const {
    filterSelector,
    setFilterAction,
    clearFilterAction,
    fetchDataAction,
    pagination,
    numberfiels,
  } = props;
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const CurrentFilterState = useAppSelector(filterSelector || (() => null));
  const isFilterRef = React.useRef(true);
  const [inputValue, setInputValue] = React.useState(props.value || "");
  const [inputField, setInputField] = React.useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        ...(pagination &&
          pagination.case === "capitalize" && {
            Limit: +pageLimit,
            Skip: 0 * +pageLimit,
          }),
        ...(pagination &&
          pagination.case === "lowercase" && {
            limit: +pageLimit,
            skip: 0 * +pageLimit,
          }),
        ...(pagination &&
          pagination.case === "uppercase" && {
            LIMIT: +pageLimit,
            SKIP: 0 * +pageLimit,
          }),
        ...Object.fromEntries(
          numberfiels
            ?.filter((field) => field in updatedFilters)
            ?.map((field) => [
              field,
              updatedFilters[field] === "" ? null : updatedFilters[field],
            ]) || []
        ),
      };

      if (fetchDataAction) {
        dispatch(fetchDataAction(payload));
      }
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...CurrentFilterState,
        [inputField]: inputValue,
      };
      if (setFilterAction) {
        dispatch(setFilterAction(updatedFilters));
      }
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...CurrentFilterState, [props.field || ""]: "" };
    if (clearFilterAction) {
      dispatch(clearFilterAction());
    }

    const payload = {
      ...updatedFilters,
      ...(pagination &&
        pagination.case === "capitalize" && {
          Limit: +pageLimit,
          Skip: currentPage * +pageLimit,
        }),
      ...(pagination &&
        pagination.case === "lowercase" && {
          limit: +pageLimit,
          skip: currentPage * +pageLimit,
        }),
      ...(pagination &&
        pagination.case === "uppercase" && {
          LIMIT: +pageLimit,
          SKIP: currentPage * +pageLimit,
        }),
      ...Object.fromEntries(
        numberfiels
          ?.filter((field) => field in updatedFilters)
          ?.map((field) => [
            field,
            updatedFilters[field] === "" ? null : +updatedFilters[field],
          ]) || []
      ),
    };

    if (fetchDataAction) {
      dispatch(fetchDataAction(payload));
    }
  };

  return (
    <td className="p-2">
      <div className="d-flex">
        <TextBox
          value={CurrentFilterState[props.field || ""] || ""}
          name={props.field || ""}
          onChange={onChange}
        />
        <Button
          svgIcon={filterClearIcon}
          title="Clear"
          disabled={!CurrentFilterState[props.field || ""]}
          onClick={onClearButtonClick}
          type="button"
          style={{
            marginLeft: "8px",
          }}
        ></Button>
      </div>
    </td>
  );
};

export default CommonFilterCell;
